import React, {useRef, useState} from 'react';
import {css, styled} from 'styled-components';
import {colors} from '../../styles/colors';
import {useFormatMsg} from 'utils/useFormatMsg';
import DefaultText from './DefaultText';
import {minmax} from 'utils/minmax';
import {useOutSideRef} from 'utils/useOutSideRef';
import DefaultButton from './DefaultButton';

/**
 * 기본 선택 옵션 컴포넌트의 Props 인터페이스
 * @property {any} value - 선택된 값
 * @property {(value: any) => void} setValue - 값 설정 함수
 * @property {Array<{text: string; value: any; color?: string}>} options - 옵션 목록
 * @property {React.CSSProperties} [wrapperStyle] - 래퍼 스타일
 * @property {string} [id] - 요소 ID
 * @property {string} [placeholderText] - 플레이스홀더 텍스트
 * @property {boolean} [readOnly] - 읽기 전용 여부
 * @property {React.CSSProperties} [containerStyle] - 컨테이너 스타일
 * @property {boolean} [border] - 테두리 표시 여부
 * @property {React.CSSProperties} [buttonStyle] - 버튼 스타일
 * @property {React.Dispatch<React.SetStateAction<HTMLDivElement | null>>} [setObserverRef] - 옵저버 참조 설정 함수
 * @property {React.CSSProperties} [optionTextStyle] - 옵션 텍스트 스타일
 * @property {number} [webkitLineClamp] - 웹킷 라인 클램프
 * @property {string} [extraButtonText] - 추가 버튼 텍스트
 * @property {React.CSSProperties} [titleWrapStyle] - 제목 래퍼 스타일
 * @property {React.CSSProperties} [extraButtonStyle] - 추가 버튼 스타일
 * @property {React.CSSProperties} [innerStyle] - 내부 스타일
 * @property {() => void} [onClickExtraButton] - 추가 버튼 클릭 이벤트 핸들러
 * @property {React.CSSProperties} [buttonTextStyle] - 버튼 텍스트 스타일
 */
interface IDefaultSelectOption {
  value: any;
  setValue: (value: any) => void;
  options: {
    text: string;
    value: any;
    color?: string;
  }[];
  wrapperStyle?: React.CSSProperties;
  id?: string;
  placeholderText?: string;
  readOnly?: boolean;
  containerStyle?: React.CSSProperties;
  border?: boolean;
  buttonStyle?: React.CSSProperties;
  setObserverRef?: React.Dispatch<React.SetStateAction<HTMLDivElement | null>>;
  optionTextStyle?: React.CSSProperties;
  webkitLineClamp?: number;
  extraButtonText?: string;
  titleWrapStyle?: React.CSSProperties;
  extraButtonStyle?: React.CSSProperties;
  innerStyle?: React.CSSProperties;
  onClickExtraButton?: () => void;
  buttonTextStyle?: React.CSSProperties;
}

/**
 * 기본 선택 옵션 컴포넌트
 * 드롭다운 형태의 선택 옵션 컴포넌트입니다.
 * @param {IDefaultSelectOption} props - 기본 선택 옵션 컴포넌트의 props
 * @returns {JSX.Element} 기본 선택 옵션 컴포넌트
 */
const DefalutSelectOption = ({
  value,
  setValue,
  options,
  wrapperStyle,
  id,
  placeholderText,
  readOnly,
  containerStyle,
  border,
  buttonStyle,
  setObserverRef,
  optionTextStyle,
  webkitLineClamp,
  extraButtonText,
  titleWrapStyle,
  extraButtonStyle,
  innerStyle,
  onClickExtraButton,
  buttonTextStyle,
}: IDefaultSelectOption) => {
  const placeholder = placeholderText
    ? useFormatMsg(placeholderText) || placeholderText
    : '';

  const [listOpen, setListOpen] = useState(false);

  const ref = useRef(null);
  useOutSideRef(ref, () => setListOpen(false));

  const webkitLineClampData = webkitLineClamp
    ? {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: '-webkit-box',
        WebkitLineClamp: webkitLineClamp,
        WebkitBoxOrient: 'vertical',
      }
    : Object.create(null);

  return (
    <Container
      style={{
        cursor: !readOnly && options.length ? 'pointer' : 'default',
        ...containerStyle,
      }}
      ref={ref}>
      <Inner style={innerStyle}>
        <TitleWrap style={titleWrapStyle}>
          <Title
            onClick={() =>
              !readOnly && options.length !== 0 && setListOpen(!listOpen)
            }
            $select={(value === '' || value === null) && placeholder ? 0 : 1}
            $readOnly={readOnly ? 1 : 0}
            $border={border ? 1 : 0}
            style={buttonStyle}>
            <DefaultText
              text={
                (value === '' || value === null) && placeholder
                  ? placeholder
                  : value !== ''
                  ? options.length === 0 ||
                    !options.find(i => i.value === value)
                    ? value
                    : options.find(i => i.value === value)?.text
                  : ''
              }
              textStyle={{
                color: buttonStyle?.color
                  ? buttonStyle.color
                  : (value === '' || value === null) && placeholder
                  ? colors.lightGrey
                  : options.length !== 0 && options[0].color
                  ? options.find(i => i.value === value)?.color
                  : colors.black,
                ...webkitLineClampData,
                WebkitLineClamp: 1,
                ...buttonTextStyle,
              }}
            />
            {!readOnly && options.length !== 0 && (
              <img
                src="images/common/select_box_arrow.png"
                alt="button arrow"
                style={{
                  transform: listOpen ? 'rotate(180deg)' : '',
                }}
              />
            )}
          </Title>
          {!readOnly && listOpen && (
            <SelectUl style={wrapperStyle} id={id}>
              {options.map(item => {
                if (webkitLineClamp) {
                  return (
                    <div
                      key={item.value}
                      style={{padding: `${minmax(16)} ${minmax(24)}`}}
                      onClick={() => {
                        setValue(item.value);
                        setListOpen(false);
                      }}>
                      <DefaultText
                        key={item.value}
                        text={item.text}
                        textStyle={{
                          color:
                            value === item.value
                              ? colors.primary2
                              : colors.black,
                          ...webkitLineClampData,
                        }}
                      />
                    </div>
                  );
                } else {
                  return (
                    <DefaultText
                      key={item.value}
                      text={item.text}
                      textStyle={{
                        color: item.color
                          ? item.color
                          : value === item.value
                          ? colors.primary2
                          : colors.black,
                        padding: `${minmax(16)} ${minmax(24)}`,
                        ...optionTextStyle,
                      }}
                      onClick={() => {
                        setValue(item.value);
                        setListOpen(false);
                      }}
                    />
                  );
                }
              })}
              {setObserverRef && (
                <div ref={setObserverRef} style={{height: '0.1px', opacity: 0}}>
                  endOfList
                </div>
              )}
            </SelectUl>
          )}
        </TitleWrap>

        {extraButtonText && (
          <DefaultButton
            text={extraButtonText}
            actived={1}
            buttonStyle={{marginLeft: minmax(16), ...extraButtonStyle}}
            onClick={onClickExtraButton}
          />
        )}
      </Inner>
    </Container>
  );
};

export default DefalutSelectOption;

const Container = styled.div`
  width: 100%;
  height: ${minmax(60)};
  position: relative;
`;
const Inner = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: row;
`;
const TitleWrap = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`;
const Title = styled.div<{
  $select?: number;
  $readOnly?: number;
  $border?: number;
}>`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding-left: ${minmax(24)};
  border: solid 1px ${colors.borderGray};
  border-radius: ${minmax(10)};
  color: ${colors.darkGray};
  font-size: ${minmax(20)};
  font-weight: 400;

  img {
    height: 100%;
  }

  ${(props: any) =>
    props.$select &&
    css`
      color: ${colors.black};
    `}
  ${(props: any) =>
    !props.$readOnly &&
    props.$select &&
    props.$border &&
    css`
      border-color: ${colors.black};
    `}
`;

const SelectUl = styled.ul`
  width: 100%;
  max-height: ${minmax(200)};
  position: absolute;
  top: 105%;
  border: solid 1px ${colors.borderGray};
  border-radius: ${minmax(10)};
  background-color: ${colors.white};
  overflow-y: auto;
  z-index: 10;

  p:hover {
    background-color: #e8ecf0;
  }
  div:hover {
    background-color: #e8ecf0;
  }
`;
